<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { writable, type Writable } from 'svelte/store'

    import { isEqual } from 'lodash-es'
    import * as yup from 'yup'

    import { gettext as _, resolve, tgettext, tngettext, type TGettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import type { INotification } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { fade } from 'svelte/transition'

    import { PASSWORD_MIN_LENGTH, PASSWORD_SUGGESTIONS } from 'hydria/definitions/passwords'

    interface Props {
        tokenExpired: () => void
        close: () => void
        saved: () => void
    }

    let { tokenExpired, close, saved }: Props = $props()

    let stoken = getContext('securityToken') as Writable<string>

    let saving = $state(false)
    let changed = $state(false)
    let error: string | undefined = $state(undefined)

    const schema = yup.object().shape({
        password: yup.string().required().min(PASSWORD_MIN_LENGTH),
        passwordConfirm: yup
            .string()
            .required()
            .when('password', ([password], lschema: yup.StringSchema) => {
                // @ts-expect-error
                return lschema.test({
                    name: 'pwmatch',
                    test: (value: string) => value === password,
                })
            }),
    })

    interface PasswordEdit {
        password: string
        passwordConfirm: string
    }
    let orig: PasswordEdit = {
        password: '',
        passwordConfirm: '',
    }
    let edit: PasswordEdit = $state({ ...orig })

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(key: string, form: any) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    let formIsValid = $derived(schema.isValidSync(edit))
    let password_isValid = $derived(validateSyncAt('password', edit))
    let passwordConfirm_isValid = $derived(validateSyncAt('passwordConfirm', edit))
    let password_tooShort = $derived(edit.password.length < PASSWORD_MIN_LENGTH)

    let dirty = $derived(!isEqual(edit, orig))

    async function saveData() {
        if (!dirty || saving) return

        saving = true
        error = undefined

        let r: SimpleFetchResult

        const notifyError: INotification = {
            type: 'danger',
            title: $_('Password change'),
            text: $_('The new password could not be activated.'),
        }
        try {
            r = await simpleFetch(
                'POST',
                '/json/security/1/password',
                {
                    new_password: edit.password,
                },
                { headers: { 'x-security-token': $stoken } },
            )
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            console.error('Failed to save user information:', r)
            notify(notifyError)
            if (r && r.data && r.data.error) error = r.data.error
        } else {
            notify({
                type: 'success',
                title: $_('Password change'),
                text: $_('The password was successfully changed.'),
            })
            changed = true
            edit = { ...orig }
            saved()
        }

        saving = false
    }

    const suggestions = writable([] as TGettext[])
    function genSuggestions(password: string) {
        const pwLen = password.length
        const pwLenDiff = 16 - pwLen
        const sugs: TGettext[] = []

        if (pwLenDiff > 0) {
            sugs.push(tngettext('one character to your password', '{n} characters to your password', { n: pwLenDiff }))
        }

        for (const sug of PASSWORD_SUGGESTIONS) {
            if (password.replace(sug.ereg, '').length < 1) {
                sugs.push(sug.text)
            }
        }

        suggestions.set(sugs)
    }
    $effect(() => {
        genSuggestions(edit.password)
    })

    let changeTimer: ReturnType<typeof setTimeout> | null = null
    $effect(() => {
        if (changed) {
            if (changeTimer) clearTimeout(changeTimer)
            changeTimer = setTimeout(() => {
                // changed = false
                changeTimer = null
                close()
            }, 7000)
        }
    })

    let addedModalOpen = false
    if (!document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.add('modal-open')
        addedModalOpen = true
    }

    onDestroy(() => {
        if (changeTimer) clearTimeout(changeTimer)
        if (addedModalOpen && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (!saving) {
                close()
            }
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    saveData()
                }}
            >
                <div class="modal-header text-bg-primary">
                    <div>{$_('Password')}</div>
                </div>
                {#if changed}
                    <div class="modal-body">
                        <p>{$_('The password was successfully changed.')}</p>
                    </div>
                {:else}
                    <div class="modal-body d-grid gap-2" style="max-width: 800px">
                        <p>{$_('Nous suggérons fortement l’utlisation d’un gestionnaire de mot de passe.')}</p>

                        <div class="row">
                            <label for="password" class="col-sm-3 col-form-label">{$_('New password')}</label>
                            <div class="col-sm-9 col-md-9 col-lg-7">
                                <input type="password" class="form-control" bind:value={edit.password} id="password" disabled={saving} class:is-invalid={!password_isValid} class:is-warning={$suggestions.length} />
                                {#if password_tooShort}
                                    <span class="help-block"
                                        >{$_('We require at least {min} characters.', {
                                            min: PASSWORD_MIN_LENGTH,
                                        })}</span
                                    >
                                {:else if $suggestions.length}
                                    <div class="help-block">
                                        <p class="text-warning">{$_('We suggest adding:')}</p>
                                        <ul>
                                            {#each $suggestions as suggestion}
                                                <li class="text-warning">{$resolve(suggestion)}</li>
                                            {/each}
                                        </ul>
                                    </div>
                                {/if}
                            </div>
                        </div>

                        <div class="row">
                            <label for="passwordConfirm" class="col-sm-3 col-form-label">{$_('Verification')}</label>
                            <div class="col-sm-9 col-md-9 col-lg-7">
                                <input type="password" class="form-control" id="passwordConfirm" bind:value={edit.passwordConfirm} disabled={saving} class:is-invalid={!passwordConfirm_isValid} />
                            </div>
                        </div>

                        <div class="row top-margin">
                            <div class="offset-sm-3 col-sm-9 col-md-9 col-lg-7" style="text-align: right;">
                                {#if error}
                                    <div class="alert alert-danger">{$resolve(error)}</div>
                                {/if}
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer text-bg-light">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            disabled={saving}
                            onclick={() => {
                                close()
                            }}>{$_('Cancel')}</button
                        >
                        <button type="submit" class="btn btn-primary mobile-100" disabled={saving || !dirty || !formIsValid}>
                            {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {$_('Saving…')}
                            {:else}{$_('Submit changes')}
                            {/if}
                        </button>
                    </div>
                {/if}
            </form>
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            if (!saving) close()
        }}
        aria-hidden="true"
    ></div>
</div>
